import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';
import {
  AdminElem, AdminLathatosag, AdminSzintService, adminTeruletek, elemLathato, torzsHelyE,
} from './admin-katalogus';

export interface AdminMenuPont {
  cim: string;
  ut: string;
  leiras: string;
  /** Kereséshez: cím + leírás + kulcsszó + alcsoport, ékezet nélkül, kisbetűvel. */
  kereso: string;
  terulet: string;
  teruletCim: string;
  alcsoport: string;
}

export interface AdminMenuTerulet {
  kulcs: string;
  cim: string;
  leiras: string;
  kep: string;
  db: number;
  alcsoportok: { cim: string; pontok: AdminMenuPont[] }[];
}

/** Ékezet nélkül, kisbetűvel – a kereső „bizonyitvany”-ra is megtalálja a „Bizonyítvány”-t. */
export function normal(s: string): string {
  return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

const HASZNALAT_KULCS = 'admin-hasznalat';
const TERULET_KULCS = 'admin-terulet';

/**
 *  AZ ADMIN MENÜJE – egy helyen számolva (admin-katalogus.ts › adminTeruletek), a keretnek
 *  (területlista, kereső) és a kezdőlapnak (a terület kártyái, gyakran használt).
 *
 *  Láthatóság: ugyanaz a szabály, mint a régi felső menüé – a jogkörszerkesztő (`hozzaferesek`),
 *  a SULINFORM-on kívül a kiszolgáló admin-döntése (`AdminSzintService`); a törzsadat-táblák a
 *  Törzsadatok jogával, a torzs_meta kategóriájuk szerinti alcsoportban. Üres alcsoport és
 *  terület nem látszik.
 *
 *  A „gyakran használt” a böngészőben számolt (localStorage) – eszközönkénti kényelmi adat,
 *  ha nem elérhető, egyszerűen nincs gyakran használt sor.
 */
@Injectable({ providedIn: 'root' })
export class AdminMenuService {
  private tar: { kulcs: string; p: Promise<AdminMenuTerulet[]> } | null = null;

  constructor(private data: DataService, private http: HttpClient, private adminSzint: AdminSzintService) {}

  /** A látható területek. `frissit`: újraszámol (pl. jogkörváltás után a keret megnyitásakor). */
  async teruletek(frissit = false): Promise<AdminMenuTerulet[]> {
    const mode = String((await this.data.getFromStorage('currentMode')) || 'SULINFORM');
    const fe = String((await this.data.getFromStorage('sajatIntezmeny'))?.intezmenyId || '');
    const kulcs = `${mode}|${fe}`;
    if (frissit || !this.tar || this.tar.kulcs !== kulcs) {
      this.tar = { kulcs, p: this.epit(mode).catch((e) => { this.tar = null; throw e; }) };
    }
    return this.tar.p;
  }

  private async epit(mode: string): Promise<AdminMenuTerulet[]> {
    const sulinform = mode === 'SULINFORM';
    const [jog, szint, torzs] = await Promise.all([
      this.data.hozzaferesek(),
      // A SULINFORM-ban a `*` maga az admin – ott nem kérdezünk (a régi viselkedés).
      sulinform ? Promise.resolve({ modulAdmin: null, altalanosAdmin: null }) : this.adminSzint.szintek(mode),
      sulinform
        ? firstValueFrom(this.http.get<any[]>('/api/torzs/meta')).catch(() => [] as any[])
        : Promise.resolve([] as any[]),
    ]);
    const l: AdminLathatosag = { szabad: jog.szabad, sulinform, ...szint };
    const torzsSzabad = l.szabad('/admin/torzsadat');
    const kategoriank: Record<string, { table_name: string; display_name: string }[]> = {};
    for (const t of torzs || []) {
      if (!t?.menu_kategoria) continue;
      (kategoriank[t.menu_kategoria] ||= []).push(t);
    }

    const ki: AdminMenuTerulet[] = [];
    for (const t of adminTeruletek(mode)) {
      const alcsoportok: AdminMenuTerulet['alcsoportok'] = [];
      for (const a of t.alcsoportok) {
        const pontok: AdminMenuPont[] = [];
        const pont = (cim: string, ut: string, leiras: string, kulcsszo = '') => pontok.push({
          cim, ut, leiras, terulet: t.kulcs, teruletCim: t.cim, alcsoport: a.cim,
          //  A terület neve nem: a „…és bizonyítvány” a terület MINDEN pontját hozná a „bizonyítvány”-ra.
          kereso: normal([cim, leiras, kulcsszo, a.cim].join(' ')),
        });
        for (const e of a.elemek) {
          if (torzsHelyE(e)) {
            if (!torzsSzabad) continue;
            for (const k of a.torzsKategoriak || []) {
              for (const tabla of kategoriank[k] || []) {
                pont(tabla.display_name, '/admin/torzsadat?table=' + tabla.table_name, 'Törzsadat-tábla', 'törzsadat');
              }
            }
          } else if (elemLathato(e, l)) {
            const el = e as AdminElem;
            pont(el.cim, el.ut, el.leiras || '', el.kulcsszo || '');
          }
        }
        if (pontok.length) alcsoportok.push({ cim: a.cim, pontok });
      }
      if (!alcsoportok.length) continue;
      ki.push({
        kulcs: t.kulcs, cim: t.cim, leiras: t.leiras, kep: t.kep, alcsoportok,
        db: alcsoportok.reduce((n, a) => n + a.pontok.length, 0),
      });
    }
    return ki;
  }

  /** Minden pont egy listában (kereséshez, egyezéshez). */
  pontok(teruletek: AdminMenuTerulet[]): AdminMenuPont[] {
    return teruletek.flatMap((t) => t.alcsoportok.flatMap((a) => a.pontok));
  }

  /** Keresés: minden szónak szerepelnie kell; a címben talált elöl. */
  keres(teruletek: AdminMenuTerulet[], q: string, max = 12): AdminMenuPont[] {
    const szavak = normal(q).split(/\s+/).filter(Boolean);
    if (!szavak.length) return [];
    const talalt = this.pontok(teruletek).filter((pt) => szavak.every((sz) => pt.kereso.includes(sz)));
    const cimben = (pt: AdminMenuPont) => (szavak.every((sz) => normal(pt.cim).includes(sz)) ? 0 : 1);
    return talalt.sort((a, b) => cimben(a) - cimben(b)).slice(0, max);
  }

  /** Melyik pont nyílt meg ezen az útvonalon: előbb pontos (query-vel), aztán query nélkül. */
  pontUrlbol(teruletek: AdminMenuTerulet[], rawUrl: string): AdminMenuPont | null {
    const url = String(rawUrl || '').split('#')[0];
    const ut = url.split('?')[0];
    const mind = this.pontok(teruletek);
    return mind.find((pt) => pt.ut === url) || mind.find((pt) => pt.ut.split('?')[0] === ut) || null;
  }

  // ── gyakran használt, utolsó terület (böngészőnként) ──────────────────────────────────────
  private olvas<T>(kulcs: string, alap: T): T {
    try { const v = localStorage.getItem(kulcs); return v ? JSON.parse(v) as T : alap; } catch { return alap; }
  }
  private ir(kulcs: string, ertek: unknown) {
    try { localStorage.setItem(kulcs, JSON.stringify(ertek)); } catch { /* privát ablak: nincs mit tenni */ }
  }

  rogzitHasznalat(ut: string) {
    if (!ut) return;
    const h = this.olvas<Record<string, number>>(HASZNALAT_KULCS, {});
    h[ut] = (h[ut] || 0) + 1;
    this.ir(HASZNALAT_KULCS, h);
  }

  gyakori(teruletek: AdminMenuTerulet[], db = 6): AdminMenuPont[] {
    const h = this.olvas<Record<string, number>>(HASZNALAT_KULCS, {});
    const mind = this.pontok(teruletek);
    return Object.entries(h)
      .sort((a, b) => b[1] - a[1])
      .map(([ut]) => mind.find((pt) => pt.ut === ut))
      .filter((pt): pt is AdminMenuPont => !!pt)
      .slice(0, db);
  }

  utolsoTerulet(): string { return this.olvas<string>(TERULET_KULCS, ''); }
  mentTerulet(kulcs: string) { if (kulcs) this.ir(TERULET_KULCS, kulcs); }
}
